<template>
  <div class="keno-board">
    <div class="left16">
      <div class="area bg1" :style="bg1Style"></div>

      <div class="area prevBox">
        <div class="prevIssue">#{{ safeIssue(prevIssueShort) }}</div>
        <div class="prevTotal"><span>{{ $t('lotto.total') }} :</span> <b>{{ prevTotalText }}</b></div>
      </div>

      <div class="area spinnerBox">
        <div class="spinner" :style="spinnerStyle">
          <div class="spinnerSec" :class="{ danger: displaySeconds > 0 && displaySeconds <= 6 }">
            {{ displaySeconds }}
          </div>
        </div>
      </div>

      <div class="area nowHead">
        <div class="nowLine1">
          <span class="issueTime">
            #{{ safeIssue(currentIssueShort) }}
            <span class="time">{{ timeText }}</span>
          </span>
        </div>
        <div class="nowLine2">{{ $t('lotto.underover') }}</div>
      </div>

      <div class="area numBox">
        <table class="numTable" cellspacing="0" cellpadding="0">
          <tbody>
          <tr v-for="r in 8" :key="'nr' + r">
            <td
                v-for="c in 10"
                :key="'nc' + r + '-' + c"
                class="numTd"
                :class="{ hit: isHit(((r - 1) * 10 + c)) }"
            >
              <span class="numText">{{ (r - 1) * 10 + c }}</span>
            </td>
          </tr>
          </tbody>
        </table>
      </div>

      <div class="area drawBox">
        <div v-if="localPhase === 'BETTING'" class="drawBg2" :style="bg2Style"></div>

        <div v-else class="drawLive">
          <div class="drawStep">{{ localDrawIndex }}</div>

          <div class="drawMid">
            <div class="rollStage">
              <div v-show="showFinalBall" class="finalBall confirmed">
                <span class="finalNum">{{ displayRollingNumber }}</span>
              </div>

              <div v-show="!showFinalBall" class="rollingPlain">
                {{ displayRollingNumber }}
              </div>
            </div>
          </div>

          <div class="drawBottom">
            <div class="sumBar">
              <span class="sumVal">{{ localSum }}</span>
            </div>
          </div>
        </div>
      </div>

      <div class="area ouBox">
        <div class="ouCards">
          <div class="ouCard under" :class="[{ disabled: !uiBettingAllowed }]" @click="pick('UNDER')">
            <div class="ouTop" :class="winFlashClass('UNDER')">
              <div class="ouLetter">{{ $t('lotto.under') }}</div>
            </div>

            <div class="ouBottom">
              <div class="ouRange">{{ underRange }}</div>
              <div class="ouOdds">{{ underOdds }}</div>
            </div>
          </div>

          <div class="ouCard over" :class="[{ disabled: !uiBettingAllowed }]" @click="pick('OVER')">
            <div class="ouTop" :class="winFlashClass('OVER')">
              <div class="ouLetter">{{ $t('lotto.over') }}</div>
            </div>

            <div class="ouBottom">
              <div class="ouRange">{{ overRange }}</div>
              <div class="ouOdds">{{ overOdds }}</div>
            </div>
          </div>
        </div>
      </div>

      <div class="area rangeBox">
        <div class="rangeTitle">{{ $t('lotto.rangeTitle') }}</div>

        <div class="rangeCards">
          <div
              class="rangeCard"
              v-for="rr in ranges"
              :key="rr.code"
              :class="[{ disabled: !uiBettingAllowed }]"
              @click="pick(rr.code)"
          >
            <div class="rangeTop" :class="winFlashClass(rr.code)">
              <div class="rangeNum">{{ rr.label }}</div>
            </div>

            <div class="rangeInfo">
              <div class="rangeRange">{{ rr.min }}-{{ rr.max }}</div>
              <div class="rangeOdds">{{ rr.odds }}</div>
            </div>
          </div>
        </div>
      </div>

      <div class="area ouRoad">
        <table class="roadTable" cellspacing="0" cellpadding="0">
          <tbody>
          <tr v-for="ri in 6" :key="'ou-r' + ri">
            <td v-for="ci in 20" :key="'ou-c' + ri + '-' + ci" class="roadTd">
                <span
                    v-if="cell(ouGrid, ri - 1, ci - 1)"
                    class="roadOU"
                    :class="ouClass(cell(ouGrid, ri - 1, ci - 1))"
                />
            </td>
          </tr>
          </tbody>
        </table>
      </div>
    </div>

    <div class="right16">
      <div class="logoArea">
        <img :src="logoImg" class="logoImg" alt="logo" />
      </div>

      <div class="rangeRoadArea">
        <table class="roadTable roadTable--grid" cellspacing="0" cellpadding="0">
          <tbody>
          <tr v-for="ri in 6" :key="'rg-r' + ri">
            <td v-for="ci in 20" :key="'rg-c' + ri + '-' + ci" class="roadTd">
                <span v-if="cell(rangeGrid, ri - 1, ci - 1)" class="roadRange">
                  <img
                      :src="rangeIcon(cell(rangeGrid, ri - 1, ci - 1))"
                      class="rangeIcon"
                      alt=""
                  />
                </span>
            </td>
          </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<script>
import bg1 from "@/assets/img/banner_1.jpg";
import bg2 from "@/assets/img/banner_2.jpg";
import spinner1 from "@/assets/img/spinner1.gif";
import logo from "@/assets/img/logo.png";

import imgUnder from "@/assets/img/under.png";
import imgOver from "@/assets/img/over.png";

import img1st from "@/assets/img/1st.png";
import img2nd from "@/assets/img/2nd.png";
import img3rd from "@/assets/img/3rd.png";
import img4th from "@/assets/img/4th.png";
import img5th from "@/assets/img/5th.png";

export default {
  name: "CenterBoard",
  props: {
    config: { type: Object, default: () => ({}) },

    bettingPhase: { type: String, default: "BETTING" },
    uiBettingAllowed: { type: Boolean, default: false },
    betSecondsLeft: { type: Number, default: 0 },
    displaySeconds: { type: Number, default: 0 },
    timeText: { type: String, default: "" },
    drawIndex: { type: Number, default: 0 },
    prevIssueShort: { type: String, default: "-" },
    prevTotal: { type: [String, Number], default: "-" },
    currentIssueShort: { type: String, default: "-" },

    ouGrid: { type: Array, default: () => [] },
    rangeGrid: { type: Array, default: () => [] },

    hitNumbers: { type: Array, default: () => [] },
    boardHitNumbers: { type: Array, default: () => [] },

    drawRollingNumber: { type: Number, default: 0 },
    drawSum: { type: Number, default: 0 },
    drawSecondsLeftText: { type: String, default: "-" },
    winCode: { type: String, default: "" }
  },

  data() {
    return {
      bg1,
      bg2,
      spinner1,
      logoImg: logo,
      imgUnder,
      imgOver,
      icons: { "1": img1st, "2": img2nd, "3": img3rd, "4": img4th, "5": img5th },
      replaying: false,
      pendingResult: null,

      localPhase: "BETTING",
      localHitNumbers: [],
      localDrawIndex: 0,
      localRolling: 0,
      localSum: 0,
      localWinCode: "",
      _flashTimer: null,
      flashKeys: []
    };
  },

  beforeDestroy() {
    if (this._flashTimer) clearTimeout(this._flashTimer);
  },

  computed: {
    bg1Style() {
      return { backgroundImage: `url(${this.bg1})` };
    },
    bg2Style() {
      return { backgroundImage: `url(${this.bg2})` };
    },
    spinnerStyle() {
      return { backgroundImage: `url(${this.spinner1})` };
    },

    prevTotalText() {
      const v = this.prevTotal;
      if (v === null || v === undefined || v === "") return "-";
      return String(v);
    },

    underRange() {
      const u = this.config && this.config.under ? this.config.under : null;
      if (!u || u.min == null || u.max == null) return "-";
      return `${u.min}-${u.max}`;
    },

    overRange() {
      const o = this.config && this.config.over ? this.config.over : null;
      if (!o || o.min == null || o.max == null) return "-";
      return `${o.min}-${o.max}`;
    },

    underOdds() {
      const u = this.config && this.config.under ? this.config.under : null;
      if (!u || u.odds == null) return "-";
      return String(u.odds);
    },

    overOdds() {
      const o = this.config && this.config.over ? this.config.over : null;
      if (!o || o.odds == null) return "-";
      return String(o.odds);
    },

    confirmedNum() {
      if (!this.localDrawIndex || this.localDrawIndex < 1) return null;
      const arr = Array.isArray(this.localHitNumbers) ? this.localHitNumbers : [];
      const v = arr[this.localDrawIndex - 1];
      if (v === null || v === undefined || v === "") return null;
      return Number(v);
    },

    showFinalBall() {
      if (this.localPhase === "BETTING") return false;
      const cn = this.confirmedNum;
      if (!cn) return false;
      const rn = Number(this.localRolling);
      if (rn && rn !== cn) return false;
      return true;
    },

    displayRollingNumber() {
      const cn = this.confirmedNum;
      if (cn) return String(cn);
      return this.localRolling > 0 ? String(this.localRolling) : "";
    },

    ranges() {
      const arr = this.config && Array.isArray(this.config.ranges) ? this.config.ranges : [];
      if (arr.length) {
        return arr.map((x) => ({
          code: x.code || "R?",
          label: x.label || String((x.code || "").replace("R", "")) || "-",
          min: x.min != null ? x.min : "-",
          max: x.max != null ? x.max : "-",
          odds: x.odds != null ? String(x.odds) : "-"
        }));
      }
      return [
        { code: "R1", label: "1", min: "-", max: "-", odds: "-" },
        { code: "R2", label: "2", min: "-", max: "-", odds: "-" },
        { code: "R3", label: "3", min: "-", max: "-", odds: "-" },
        { code: "R4", label: "4", min: "-", max: "-", odds: "-" },
        { code: "R5", label: "5", min: "-", max: "-", odds: "-" }
      ];
    },

    isConfirmedBall() {
      if (this.bettingPhase === "BETTING") return false;
      if (!this.drawIndex || this.drawIndex < 1) return false;
      return Array.isArray(this.hitNumbers) && this.hitNumbers.length >= this.drawIndex;
    }
  },

  watch: {
    bettingPhase: {
      immediate: true,
      handler(v) {
        if (this.replaying) return;

        if (v === "DRAWING") {
          this.localPhase = "DRAWING";
        } else if (v === "BETTING") {
          this.localPhase = "BETTING";
        }
      }
    },

    boardHitNumbers: {
      immediate: true,
      handler(v) {
        if (!this.replaying && this.localPhase === "BETTING") {
          this.localHitNumbers = Array.isArray(v) ? [...v] : [];
        }
      }
    },

    hitNumbers: {
      immediate: true,
      handler(v) {
        if (!this.replaying && this.localPhase !== "BETTING") {
          this.localHitNumbers = Array.isArray(v) ? [...v] : [];
        }
      }
    },

    drawIndex: {
      immediate: true,
      handler(v) {
        if (!this.replaying) this.localDrawIndex = Number(v) || 0;
      }
    },

    drawRollingNumber: {
      immediate: true,
      handler(v) {
        if (!this.replaying) this.localRolling = Number(v) || 0;
      }
    },

    drawSum: {
      immediate: true,
      handler(v) {
        if (!this.replaying) this.localSum = Number(v) || 0;
      }
    },

    winCode: {
      immediate: false,
      handler(v) {
        if (this.replaying) return;
        this.localWinCode = v || "";
        this.triggerFlash(this.localWinCode);
      }
    }
  },

  mounted() {
    if (!this.replaying) {
      this.localPhase = this.bettingPhase === "DRAWING" ? "DRAWING" : "BETTING";
    }

    if (this.localPhase === "BETTING") {
      this.localHitNumbers = Array.isArray(this.boardHitNumbers) ? [...this.boardHitNumbers] : [];
    } else {
      this.localHitNumbers = Array.isArray(this.hitNumbers) ? [...this.hitNumbers] : [];
    }

    this.localDrawIndex = Number(this.drawIndex) || 0;
    this.localRolling = Number(this.drawRollingNumber) || 0;
    this.localSum = Number(this.drawSum) || 0;
    this.localWinCode = this.winCode || "";
  },

  methods: {
    onDrawFinal(result) {
      this.pendingResult = result;
      if (!this.replaying) this.playResultReplay();
    },

    triggerFlash(v) {
      if (!v) return;

      const keys = String(v)
          .split("|")
          .map(s => s.trim())
          .filter(Boolean);

      this.flashKeys = keys;

      if (this._flashTimer) clearTimeout(this._flashTimer);
      this._flashTimer = setTimeout(() => {
        this.flashKeys = [];
      }, 5200);
    },

    sleep(ms) {
      return new Promise(r => setTimeout(r, ms));
    },

    async playResultReplay() {
      if (!this.pendingResult) return;

      const result = this.pendingResult;
      this.pendingResult = null;

      this.replaying = true;

      this.localPhase = "DRAWING";
      this.localHitNumbers = [];
      this.localDrawIndex = 0;
      this.localSum = 0;
      this.localRolling = 0;

      const ROLL_MS = 900;
      const TICK_MS = 60;
      const HOLD_MS = 400;
      const GAP_MS = 140;

      const nums = Array.isArray(result.nums) ? result.nums : [];

      for (let i = 0; i < nums.length; i++) {
        this.localDrawIndex = i + 1;

        const end = Date.now() + ROLL_MS;
        while (Date.now() < end) {
          this.localRolling = 1 + Math.floor(Math.random() * 80);
          await this.sleep(TICK_MS);
        }

        const n = Number(nums[i]);
        this.localRolling = n;
        this.localHitNumbers = [...this.localHitNumbers, n];
        this.localSum = this.localHitNumbers.reduce((a, b) => a + b, 0);

        await this.sleep(HOLD_MS);
        await this.sleep(GAP_MS);
      }

      this.localWinCode = result.winCode || "";
      this.triggerFlash(this.localWinCode);
      await this.sleep(1800);

      this.localPhase = "BETTING";
      this.replaying = false;

      // ✅ 播完切回 BETTING 后，立刻恢复上一期开奖结果到 80 个号码区
      this.localHitNumbers = Array.isArray(this.boardHitNumbers) ? [...this.boardHitNumbers] : [];

      if (this.pendingResult) this.playResultReplay();
    },

    safeIssue(v) {
      if (v === null || v === undefined) return "-";
      const s = String(v);
      return s ? s : "-";
    },

    winFlashClass(code) {
      if (!this.flashKeys || !this.flashKeys.length) return "";
      return this.flashKeys.includes(code) ? "winFlash" : "";
    },

    pick(code) {
      if (!this.uiBettingAllowed) return;
      this.$emit("pick", { code });
    },

    isHit(n) {
      const arr = this.localHitNumbers || [];
      for (let i = 0; i < arr.length; i++) {
        if (Number(arr[i]) === Number(n)) return true;
      }
      return false;
    },

    cell(grid, r, c) {
      if (!grid || !grid.length) return null;
      if (!grid[r] || !grid[r].length) return null;
      return grid[r][c] ? grid[r][c] : null;
    },

    ouClass(cellObj) {
      const v = cellObj && (cellObj.value || cellObj.key || cellObj);
      if (String(v).toUpperCase() === "OVER") return "is-over";
      return "is-under";
    },

    rangeIcon(cellObj) {
      const v = cellObj && (cellObj.value || cellObj.key || cellObj);
      const s = String(v).replace("R", "");
      return this.icons[s] ? this.icons[s] : this.icons["1"];
    }
  }
};
</script>

<style scoped>
.keno-board{
  display:flex;
  gap:10px;
  padding:0;
  background:#ffffff;
  width:max-content;
  overflow:hidden;
}

.left16{
  width: 767px;
  height: 577px;
  flex: 0 0 767px;
  border: 1px solid #96afde;
  background: #f7fbff;
  display: grid;
  grid-template-columns:
    repeat(10, 36px)
    repeat(2, 39px)
    repeat(4, 79px);
  grid-template-rows: 56px 289px 228px;
  gap: 0;
  box-sizing: border-box;
  overflow: hidden;
}

.right16{
  width: 767px;
  height: 577px;
  flex: 0 0 767px;
  border: 1px solid #96afde;
  border-top: none;
  background: #ffffff;
  display: grid;
  grid-template-rows: 344px 229px;
  overflow: hidden;
  box-sizing: border-box;
}

.logoArea{
  grid-row: 1;
  display:flex;
  align-items:center;
  justify-content:center;
  background:#ffffff;
  border-bottom: 2px solid #96afde;
}

.rangeRoadArea{
  grid-row: 2;
  background:#ffffff;
}

.logoImg{
  display:block;
  width: 320px;
  max-width: 90%;
}

.area{ box-sizing: border-box; }

.bg1{
  grid-column: 1 / span 5;
  grid-row: 1;
  background-size: cover;
  background-position: center;
  border-right: 1px solid #96afde;
  border-bottom: 1px solid #96afde;
}

.prevBox{
  position: relative !important;
  width: 180px !important;
  height: 56px !important;
  padding: 0 !important;
  background: #3c5aa0 !important;
  color: #ffffff !important;
  box-sizing: border-box !important;
}

.prevBox .prevIssue{
  position: absolute !important;
  left: 0 !important;
  right: 0 !important;
  top: 0 !important;
  height: 28px !important;
  line-height: 28px !important;
  text-align: center !important;
  font-size: 15px !important;
  font-weight: bold !important;
  color: #ffffff !important;
  white-space: nowrap !important;
  overflow: hidden !important;
  text-overflow: ellipsis !important;
}

.prevBox .prevTotal{
  position: absolute !important;
  left: 0 !important;
  right: 0 !important;
  top: 28px !important;
  height: 28px !important;
  line-height: 28px !important;
  display: flex !important;
  align-items: center !important;
  justify-content: space-between !important;
  padding: 0 10px !important;
  box-sizing: border-box !important;
  font-size: 16px !important;
  font-weight: bold !important;
  color: #ffffff !important;
  white-space: nowrap !important;
}

.prevBox .prevTotal span{
  opacity: 0.95 !important;
}

.prevBox .prevTotal b{
  font-weight: bold !important;
  font-size: 16px !important;
}

.spinnerBox{
  grid-column: 11 / span 2;
  grid-row: 1;
  background: transparent;
  padding: 0;
  overflow: hidden;
}

.spinner{
  width: 100%;
  height: 100%;
  background-position: center;
  background-repeat: no-repeat;
  margin: -1px;
  background-size: 102% 102%;
  display:flex;
  align-items:flex-end;
  justify-content:center;
  overflow: hidden;
}

.spinnerSec{
  margin-bottom: 6px;
  font-weight: 600;
  color: #ffffff;
  font-size: 15px;
  line-height: 18px;
}

.spinnerSec.danger{
  color: #ff3b30;
  text-shadow: 0 1px 6px rgba(255,0,0,.5);
}

.nowHead{
  grid-column: 13 / span 4;
  grid-row: 1;
  display: flex;
  flex-direction: column;
  padding: 0;
}

.nowLine1{
  height: 28px;
  background: #3c5aa0;
  color: #ffffff;
  padding: 0 15px;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  font-weight: bold;
  border-bottom: 1px solid #96afde;
}

.issueTime{
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0;
  white-space: nowrap;
  color: #ffffff;
  font-size: 14px;
  font-weight: bold;
}

.time{
  color: #ffffff;
  font-size: 13px;
  font-weight: bold;
  opacity: 1;
  white-space: nowrap;
}

.nowLine2{
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #d2e5ff;
  color: #000000;
  font-size: 14px;
  font-weight: bold;
  border-top: 1px solid #96afde;
  box-sizing: border-box;
}

.numBox{
  grid-column: 1 / span 10;
  grid-row: 2;
  border-right: 1px solid #96afde;
  border-bottom: 1px solid #96afde;
  background:#ffffff;
  overflow: hidden;
}

.numTable{
  width: 100%;
  height: 100%;
  border-collapse: collapse;
  table-layout: fixed;
}

.numTable tr{
  height: calc(100% / 8);
}

.numTd{
  width: 36px;
  height: 36px;
  box-sizing: border-box;
  border: 1px solid #96afde;
  text-align: center;
  vertical-align: middle;
  font-weight: 600;
  color: rgba(58,58,58,0.9);
  background:#ffffff;
  padding: 0;
}

.numTd.hit{
  background:#ff2821;
  color:#ffffff;
}

.numText{
  font-size: 14px;
  font-weight: 600;
  line-height: 34px;
  color: rgba(58,58,58,0.9);
  display: block;
}

.drawBox{
  grid-column: 11 / span 2;
  grid-row: 2;
  height: 139px;
  align-self: start;
  border-right: 1px solid #96afde;
  border-bottom: 1px solid #96afde;
  position: relative;
  background:#ffffff;
  overflow:hidden;
}

.drawBg2{
  position:absolute;
  inset:0;
  background-size: cover;
  background-position:center;
  opacity: 1;
}

.drawLive{
  position:absolute;
  inset:0;
  background:#ffffff;
  overflow:hidden;
}

.drawStep{
  position:absolute;
  left:0;
  top:0;
  width:22px;
  height:22px;
  background:#b40000;
  color:#fff;
  font-size:12px;
  font-weight:700;
  display:flex;
  align-items:center;
  justify-content:center;
  z-index:5;
}

.drawMid{
  position:absolute;
  left:0;
  top:0;
  width:100%;
  height:90px;
  z-index:1;
}

.rollStage{
  position:absolute;
  inset:0;
  box-sizing:border-box;
  background-color:#ffe4bd;
  background-image: linear-gradient(#fbff41, #f5681e 97%, #f39321 10%);
  text-align:center;
  color:#fff;
  font-weight:700;
  font-size:30px;
  padding-top:35px;
  padding-bottom:7px;
  overflow:hidden;
}

.drawBottom{
  position:absolute;
  left:0;
  right:0;
  bottom:0;
  height:49px;
  z-index:1;
}

.sumBar{
  width:100%;
  height:100%;
  box-sizing:border-box;
  background-color:#cb0018;
  background-image: linear-gradient(#7e000f, #cb0018, #7e000f 98%, #f0001c 10%);
  display:flex;
  align-items:center;
  justify-content:center;
  padding:0;
}

.sumVal{
  color:#fff;
  font-size:22px;
  font-weight:700;
  line-height:22px;
}

.finalBall{
  position:absolute;
  left:50%;
  top:45px;
  transform: translate(-50%, -50%);
  width:50px;
  height:50px;
  border-radius:50%;
  display:flex;
  align-items:center;
  justify-content:center;
  z-index:3;
  background-color:#cb0018;
  background-image: linear-gradient(#7e000f, #cb0018, #7e000f 98%, #f0001c 10%);
}

.finalBall.confirmed{
  animation: ballPop .18s ease-out 1;
}

@keyframes ballPop{
  from{ transform: translate(-50%, -50%) scale(.92); }
  to  { transform: translate(-50%, -50%) scale(1); }
}

.rollingPlain{
  position:absolute;
  left:50%;
  top:35px;
  transform: translateX(-50%);
  z-index:2;
  color:#fff;
  font-weight:700;
  font-size:30px;
  line-height:30px;
  pointer-events:none;
}

.finalNum{
  color:#fff;
  font-size:24px;
  font-weight:700;
  line-height:24px;
}

.winFlash{
  animation: winFlashYellow2ThenBlueHold9s 5s linear 1 forwards;
}

@keyframes winFlashYellow2ThenBlueHold9s{
  0%        { background: transparent; }
  11.111%   { background: #ffb300; }
  22.222%   { background: transparent; }
  33.333%   { background: #ffb300; }
  44.444%   { background: transparent; }
  44.445%   { background: #3c63b5; }
  100%      { background: #3c63b5; }
}

.ouTop.winFlash .ouLetter,
.rangeTop.winFlash .rangeNum{
  color:#ffffff !important;
}

.ouBox{
  grid-column: 13 / span 4;
  grid-row: 2;
  width: 316px;
  height: 139px;
  border-bottom: 1px solid #96afde;
  background:#ffffff;
  box-sizing: border-box;
}

.ouCards{
  width:100%;
  height:100%;
  display:flex;
}

.ouCard{
  width:158px;
  height:139px;
  display:flex;
  flex-direction:column;
  box-sizing:border-box;
  cursor:pointer;
  user-select:none;
}

.ouCard.under{
  border-right: 1px solid #96afde;
}

.ouCard.disabled,
.rangeCard.disabled{
  opacity: 1;
  cursor: not-allowed;
}

.ouTop{
  height: 90px;
  display:flex;
  align-items:center;
  justify-content:center;
}

.ouBottom{
  height: 49px;
  background:#ffffff;
  border-top: 1px solid #c0d1e5;
  display:flex;
  flex-direction:column;
  align-items:center;
  justify-content:center;
  gap: 2px;
  box-sizing:border-box;
}

.ouCard.under .ouTop{
  background: linear-gradient(to bottom, #0f00fe 0%, #5a41ec 50%, #0f00fe 100%);
}

.ouCard.over .ouTop{
  background: linear-gradient(to bottom, #990000 0%, #df1e1e 50%, #990000 100%);
}

.ouTop{
  transition: background .15s ease, box-shadow .15s ease;
}

.ouCard:hover .ouTop{
  background: #3c63b5 !important;
  box-shadow: inset 0 0 0 1px #2a4f9b;
}

.ouCard:hover .ouLetter{
  color:#ffffff;
}

.ouLetter{
  font-size: 60px;
  font-weight: bold;
  line-height: 60px;
  color:#ffffff;
}

.ouRange{
  font-size: 12px;
  font-weight: 500;
  color: #000000;
  line-height: 14px;
}

.ouOdds{
  font-size: 16px;
  font-weight: bold;
  color:#3c5aa0;
  line-height: 18px;
}

.rangeBox{
  grid-column: 11 / span 6;
  grid-row: 2;
  height: 150px;
  align-self: end;
  border-bottom: 1px solid #96afde;
  background:#ffffff;
  display:flex;
  flex-direction:column;
}

.rangeTitle{
  height: 28px;
  line-height: 28px;
  padding: 0;
  text-align: center;
  background: #d2e5ff;
  color: #000000;
  font-size: 14px;
  font-weight: bold;
  box-sizing:border-box;
  border-bottom: 1px solid #c0d1e5;
}

.rangeCards{
  flex:1;
  display:grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 0;
}

.rangeCard{
  border-right: 1px solid #96afde;
  border-top: 1px solid #96afde;
  background:#fff;
  display:flex;
  flex-direction:column;
  height: 100%;
  box-sizing:border-box;
  cursor:pointer;
  user-select:none;
  transition: background .15s ease, color .15s ease;
}

.rangeCard:nth-child(5){ border-right:0; }

.rangeTop{
  flex: 1;
  display:flex;
  align-items:center;
  justify-content:center;
  transition: background .15s ease;
}

.rangeInfo{
  height: 44px;
  border-top: 1px solid #c0d1e5;
  background:#ffffff;
  display:flex;
  flex-direction:column;
  align-items:center;
  justify-content:center;
  gap: 2px;
  box-sizing:border-box;
}

.rangeNum{
  font-size: 60px;
  font-weight: bold;
  color:#a71f2d;
  line-height: 60px;
}

.rangeRange{
  font-size: 12px;
  font-weight: 500;
  color: #000000;
  line-height: 14px;
}

.rangeOdds{
  font-size: 16px;
  font-weight: bold;
  color:#3c5aa0;
  line-height: 18px;
}

.rangeCard:hover .rangeTop{
  background: #3c63b5;
  box-shadow: inset 0 0 0 1px #2a4f9b;
}

.rangeCard:hover .rangeNum{
  color:#ffffff;
}

.ouRoad{
  grid-column: 1 / span 16;
  grid-row: 3;
  background:#ffffff;
}

.roadTable{
  border-collapse: collapse;
  table-layout: fixed;
}

.roadTd{
  width: 38px;
  height: 38px;
  box-sizing: border-box;
  border: 1px solid #96afde;
  padding: 0;
  text-align: center;
  vertical-align: middle;
  background:#f2f6f9;
}

.roadOU{
  display:block;
  width: 32px;
  height: 32px;
  margin: 2px auto;
  background-repeat: no-repeat;
  background-position: center;
  background-size: 32px 32px;
}

.roadOU.is-under{ background-image: url(~@/assets/img/under.png); }
.roadOU.is-over{ background-image: url(~@/assets/img/over.png); }

.roadRange{
  width: 100%;
  height: 100%;
  display:flex;
  align-items:center;
  justify-content:center;
}

.rangeIcon{
  display:block;
  width: 32px;
  height: 32px;
  object-fit: contain;
  margin: 2px auto;
}

.rangeRoadArea{
  height: 100%;
  overflow: hidden;
}

.rangeRoadArea .roadTable{
  width: 100%;
  height: 100%;
  border-collapse: collapse;
  table-layout: fixed;
}

.rangeRoadArea .roadTable tr{
  height: calc(100% / 6);
}

.rangeRoadArea .roadTd{
  height: calc(100% / 6);
  border: 1px solid #96afde;
  padding: 0;
  text-align: center;
  vertical-align: middle;
  background: #f2f6f9;
}

.rangeRoadArea .roadRange{
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.rangeRoadArea .rangeIcon{
  width: 32px;
  height: 32px;
  display:block;
  object-fit:contain;
  margin: 2px auto;
}
</style>